import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { Subject } from 'rxjs';
import { OrientationTypeEnum } from 'src/app/modules/shared/enums/orientation-type.enum';
import { CalculatorService } from 'src/app/services/calculator/calculator.service';
import { CalculatorEnhancedModel } from 'src/app/services/calculator/models/calculator-enhanced-model';
import {
  CalculatorProductsResultModel,
  ObesityTreatment,
  ProductCalculationResult,
  Product
} from 'src/app/services/calculator/models/calculator-product-result-model';
import { ClinicalCharacteristicItem } from 'src/app/services/clinical-characteristic/models/clinical-characteristic-model';
import { FeatureFlagService } from 'src/app/services/feature-flag/feature-flag.service';
import { PetModel } from 'src/app/services/pet/models/pet-model';
import { PrescriptionProduct } from 'src/app/services/prescription/models/prescription-model';
import { DeviceTypeEnum } from 'src/app/modules/shared/enums/device-type.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SpecieEnum } from 'src/app/modules/shared/enums/specie.enum';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ErrorTypeEnum } from 'src/app/modules/shared/enums/error-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { ProductLineEnum } from 'src/app/modules/shared/enums/product-line.enum';
import { TypeProductEnum } from 'src/app/modules/shared/enums/type-product.enum';
import { AgeGroupEnum } from 'src/app/modules/shared/enums/age-group.enum';
import { OrientationPurposeEnum } from 'src/app/modules/shared/enums/orientation-purpose.enum';
import { PurposeEnum } from 'src/app/modules/shared/enums/purpose.enum';

@Component({
  selector: 'app-suggested-alimentation-guidance',
  templateUrl: './suggested-alimentation-guidance.component.html',
  styleUrl: './suggested-alimentation-guidance.component.scss'
})
export class SuggestedAlimentationGuidanceComponent implements OnInit {
  constructor(
    private calculatorService: CalculatorService,
    private flagService: FeatureFlagService,
    private detectorDevice: DeviceDetectorService,
    private notificationService: NotificationService
  ) {
    this.weightControlFlag = this.flagService.checkFeature('weight-control');
    this.getScreenSize();
  }

  @Input() petSelected: PetModel;
  @Input() nutritionalGuidanceForm: UntypedFormGroup;
  @Input() portionsForm: UntypedFormGroup;
  @Input() selectedClinicalCharacteristics: ClinicalCharacteristicItem[] = [];
  @Input() petDataIsValid: Subject<boolean>;
  @Input() selectedPhysicalActivity: number;

  @Output() selectedAlimentationChanged = new Subject<PrescriptionProduct[]>();
  @Output() indexSelectedAlimentationChanged = new Subject<number>();
  @Output() typePrescriptionChanged = new Subject<OrientationTypeEnum>();
  @Output() obesityTreatment = new EventEmitter<ObesityTreatment>();
  @Output() isErrorCalculatorTreatmentPeriod = new EventEmitter<boolean>();
  @Output() isAfterSixtyDaysPeriod = new EventEmitter<boolean>();

  weightControlFlag = false;
  showDone = false;
  doneMessage: string;
  speciePet: number;
  listGradualExchanges: ProductCalculationResult;
  selectedAlimentation: PrescriptionProduct[];

  minQuantityWetFoodNotReached = false;
  combineFoodError = false;

  suggestedAlimentation: CalculatorProductsResultModel;

  isDryFoodSelected = true;
  isCompleteWetFoodSelected = false;
  isCookieSelected = false;
  isSnackWetFoodSelected = false;

  gradualExchange = false;

  selectedDryFood: ProductCalculationResult;
  selectedCompleteWetFood: ProductCalculationResult;
  selectedCookie: ProductCalculationResult;
  selectedSnackWetFood: ProductCalculationResult;

  wetPercentage: number = 0;
  dryPercentage: number = 0;

  isRecalculatingValues: boolean = false;
  originalDryFoodGrams: number;
  originalWetFoodGrams: number;

  dryFoodMessage: string = '';
  scoreMessage: string = 'Recomendamos a atualização do cálculo quando o paciente alcançar o seu ECC ideal de acordo com o seu nível de atividade.';
  osteoarticularDiseaseMessage: string = 'O alimento sugerido auxilia na saúde articular, devido à inclusão de sulfato de condroitina, glicosamina, beta glucanas e ômega-3.';

  get isFoodCombination(): boolean {
    return this.isDryFoodSelected && this.isCompleteWetFoodSelected;
  }

  get isBaby(): boolean {
    return this.petSelected?.faixaEtariaId === AgeGroupEnum.PUPPY;
  }

  get calculator(): CalculatorEnhancedModel {
    const calc = new CalculatorEnhancedModel();
    calc.ageGroupId = this.petSelected.faixaEtariaId;
    calc.birthDate = this.petSelected.dataNascimento;
    calc.physicalActivity =
      this.nutritionalGuidanceForm.controls.nivelAtividade.value;
    if (!this.weightControlFlag) {
      calc.clinicalCharacteristics = this.selectedClinicalCharacteristics.map(
        (c) => c.id
      );
    } else {
      calc.clinicalCharacteristics = [];
    }
    calc.gender = this.petSelected.genero;
    calc.raceId = this.petSelected.racaId;
    calc.specie =
      this.petSelected.petEspecie === 'Dog' ? SpecieEnum.DOG : SpecieEnum.CAT;
    calc.sizeId = this.petSelected.porte;
    const weightValue = this.nutritionalGuidanceForm.controls.peso.value;
    if (weightValue && typeof weightValue === 'string') {
      calc.weight = Number(weightValue.replace(',', '.'));
    }

    calc.orientationPurposeId =
      this.nutritionalGuidanceForm.controls.orientationPurposeId.value;
    calc.stageDisease =
      this.nutritionalGuidanceForm.controls.stageDisease.value;
    calc.score = this.nutritionalGuidanceForm.controls.score.value;
    calc.device = this.getdeviceType();
    calc.calculatorType = 1;
    calc.petId = this.petSelected.id;
    calc.isOrientation = true;

    if (this.isFoodCombination) {
      calc.useCombinedCompleteProducts = true;
      calc.secondaryProductId = this.selectedCompleteWetFood.product.id;
      calc.primaryProductPercentage = this.dryPercentage;
      calc.secondaryProductPercentage = this.wetPercentage;
    }

    return calc;
  }

  ngOnInit(): void {
    this.updateSpeciePet();

    this.petDataIsValid.subscribe((isValid: boolean) => {
      if (isValid) {
        this.resetFoodPercentage();
        this.getSuggestedAlimentation();
      } else {
        this.clearSuggestedAlimentation();
      }
    });
  }

  private updateSpeciePet(): void {
    if (this.petSelected) {
      this.speciePet =
        this.petSelected.petEspecie === 'Dog' ? SpecieEnum.DOG : SpecieEnum.CAT;
    } else {
      this.speciePet = SpecieEnum.DOG;
    }
  }

  isFetchingProducts = false;

  private fetchProductsFromCalculator(
    params: CalculatorEnhancedModel,
    subscribeCb: (res: CalculatorProductsResultModel) => void,
    errorCb?: (error: HttpErrorResponse) => void
  ): void {
    this.isFetchingProducts = true;
    this.calculatorService.getProducts(params).subscribe(
      (res) => {
        try {
          subscribeCb(res);
        } finally {
          this.isFetchingProducts = false;
        }
      },
      (error) => {
        errorCb?.(error);
        this.isFetchingProducts = false;
      }
    );
  }

  getSuggestedAlimentation(): void {
    this.updateSpeciePet();
    this.clearSuggestedAlimentation();
    this.fetchProductsFromCalculator(
      this.calculator,
      (res) => {
        const dryFood = res.dryFood ?? [];
        const cookies = res.cookies ?? [];
        const wetFood = res.wetFood ?? [];
        const completeWetFood = res.completeWetFood ?? [];

        this.suggestedAlimentation = {
          offerCookies: res.offerCookies,
          offerWetFood: res.offerWetFood,
          dryFood,
          cookies,
          wetFood,
          completeWetFood,
          obesityTreatment: res.obesityTreatment,
          dryFoodMessage: res.dryFoodMessage,
          dryFoodAlertMessage: res.dryFoodAlertMessage,
          canChangeDryProduct: res.canChangeDryProduct
        };
        const hasDryFood = this.suggestedAlimentation.dryFood.length > 0;

        if (!dryFood.length && !completeWetFood.length) {
          this.selectedDryFood = null;
          this.selectedCompleteWetFood = null;
          this.selectedCookie = null;
          this.selectedSnackWetFood = null;
          this.listGradualExchanges = null;
          this.updateSelectedAlimentation();
          return;
        }

        this.isCookieSelected = false;
        this.isSnackWetFoodSelected = false;
        this.isDryFoodSelected = !!dryFood.length;
        this.isCompleteWetFoodSelected = !dryFood.length && !!completeWetFood.length;

        this.originalDryFoodGrams = dryFood[0]?.gramsPerDay ?? 0;
        this.originalWetFoodGrams = completeWetFood[0]?.gramsPerDay ?? 0;

        this.obesityTreatment.emit(this.suggestedAlimentation.obesityTreatment);
        if (this.suggestedAlimentation.obesityTreatment?.message) {
          if (this.suggestedAlimentation.obesityTreatment.message.toast) {
            this.notificationService.warning(
              this.suggestedAlimentation.obesityTreatment.message.message
            );
          }
        }

        this.doneMessage =
          this.speciePet == SpecieEnum.DOG
            ? 'Eba! Peso Meta atingido.'
            : 'Eba! Redução de Peso completa.';
        this.showDone =
          !!this.suggestedAlimentation.obesityTreatment?.isComplete;

        this.selectedDryFood = dryFood[0] ?? null;

        this.selectedCompleteWetFood = completeWetFood[0] ?? null;

        this.selectedCookie = cookies[0] ?? null;

        this.selectedSnackWetFood = wetFood[0] ?? null;

        this.listGradualExchanges = dryFood[0] ?? completeWetFood[0] ?? null;

        if (dryFood.length) {
          dryFood[0].selected = true;
          this.indexSelectedAlimentationChanged.next(0);
        } else if (completeWetFood.length) {
          completeWetFood[0].selected = true;
        }

        if (res.dryFoodAlertMessage) {
          this.notificationService.warning(res.dryFoodAlertMessage);
        }

        this.dryFoodMessage =
          res.dryFoodMessage && res.dryFoodMessage !== this.scoreMessage
            ? res.dryFoodMessage
            : '';

        this.inicializePortionsForm();

        this.updateTypePrescription();
        this.updateSelectedAlimentation();
        this.isErrorCalculatorTreatmentPeriod.emit(false);
        this.isAfterSixtyDaysPeriod.emit(false);
      },
      (errors: HttpErrorResponse) => {
        const typeError = errors.error.errorType;

        switch (typeError) {
          case ErrorTypeEnum.CALCULATOR_MINIMUM_TREATMENT_PERIOD:
            this.isErrorCalculatorTreatmentPeriod.emit(true);
            break;
          case ErrorTypeEnum.CALCULATOR_MAXIMUM_TREATMENT_PERIOD:
            this.isAfterSixtyDaysPeriod.emit(true);
            break;
        }
      }
    );
  }

  clearSuggestedAlimentation(): void {
    this.suggestedAlimentation = null;
    this.clearPortionsForm();
    this.updateSelectedAlimentation();
  }

  clearPortionsForm(): void {
    const premierPortions = this.portionsForm.controls
      .premierPortions as UntypedFormArray;
    const premierGrams = this.portionsForm.controls
      .premierGrams as UntypedFormArray;
    const portionsForm = this.portionsForm.controls
      .portions as UntypedFormArray;
    const gramsForm = this.portionsForm.controls.grams as UntypedFormArray;
    const premierPortionsWet = this.portionsForm.controls
      .premierPortionsWet as UntypedFormArray;
    const premierGramsWet = this.portionsForm.controls
      .premierGramsWet as UntypedFormArray;
    const portionsFormWet = this.portionsForm.controls
      .portionsWet as UntypedFormArray;
    const gramsFormWet = this.portionsForm.controls
      .gramsWet as UntypedFormArray;
    while (premierPortions.length !== 0) {
      premierPortions.removeAt(0);
    }
    while (premierGrams.length !== 0) {
      premierGrams.removeAt(0);
    }
    while (portionsForm.length !== 0) {
      portionsForm.removeAt(0);
    }
    while (gramsForm.length !== 0) {
      gramsForm.removeAt(0);
    }
    while (premierPortionsWet.length !== 0) {
      premierPortionsWet.removeAt(0);
    }
    while (premierGramsWet.length !== 0) {
      premierGramsWet.removeAt(0);
    }
    while (portionsFormWet.length !== 0) {
      portionsFormWet.removeAt(0);
    }
    while (gramsFormWet.length !== 0) {
      gramsFormWet.removeAt(0);
    }
  }

  updateSelectedAlimentation(): void {
    const selectedAlimentation: PrescriptionProduct[] = [];
    if (this.shouldHideTreatsSelection) {
      this.isCookieSelected = false;
      this.isSnackWetFoodSelected = false;
    }

    const indexSuggestedAlimentation =
      this.suggestedAlimentation?.dryFood?.findIndex((x) => x.selected);
    const dryFood =
      this.suggestedAlimentation?.dryFood[indexSuggestedAlimentation];

    const useCombinedCompleteProducts = this.isFoodCombination;

    if (this.isDryFoodSelected && indexSuggestedAlimentation >= 0 && dryFood) {
      selectedAlimentation.push({
        id: dryFood.product.id,
        name: dryFood.product.name,
        sku: dryFood.product.sku,
        shortName: dryFood.product.shortName,
        type: dryFood.product.productTypeId,
        imageUrl: dryFood.product.productImageUrl,
        premierSuggestionQty: this.getAlimentationGrams(dryFood),
        premierSuggestionPerDay: dryFood.suggestedPortionAmount,
        vetQty: this.portionsForm.value.grams[indexSuggestedAlimentation],
        vetPerDay: this.portionsForm.value.portions[indexSuggestedAlimentation],
        isCompleteFood: dryFood.product.isCompleteFood,
        useCombinedCompleteProducts,
        ...(useCombinedCompleteProducts && {
          combinedProductId: this.selectedCompleteWetFood.product.id,
          productPercentage: this.dryPercentage
        })
      });
    }

    if (this.isCompleteWetFoodSelected && this.selectedCompleteWetFood) {
      const indexSuggestedWet =
        this.suggestedAlimentation?.completeWetFood?.findIndex(
          (x) => x.selected
        );
      const completeWetFood =
        this.suggestedAlimentation?.completeWetFood?.[indexSuggestedWet];
      if (!completeWetFood?.product) {
        this.selectedAlimentation = selectedAlimentation;
        this.selectedAlimentationChanged.next(this.selectedAlimentation);
        return;
      }
      const wetGramsArray = this.portionsForm.controls
        .gramsWet as UntypedFormArray;
      const wetPortionsArray = this.portionsForm.controls
        .portionsWet as UntypedFormArray;
      const wetVetQty =
        wetGramsArray?.controls[indexSuggestedWet]?.value ??
        completeWetFood?.gramsPerDay;
      const wetVetPerDay =
        wetPortionsArray?.controls[indexSuggestedWet]?.value ?? 1;

      selectedAlimentation.push({
        id: completeWetFood.product.id,
        name: completeWetFood.product.name,
        sku: completeWetFood.product.sku,
        shortName: completeWetFood.product.shortName,
        type: completeWetFood.product.productTypeId,
        imageUrl: completeWetFood.product.productImageUrl,
        premierSuggestionQty: this.getAlimentationGrams(completeWetFood),
        premierSuggestionPerDay: completeWetFood.suggestedPortionAmount,
        vetQty: wetVetQty,
        vetPerDay: wetVetPerDay,
        isCompleteFood: completeWetFood.product.isCompleteFood,
        useCombinedCompleteProducts,
        ...(useCombinedCompleteProducts &&
          dryFood && {
            combinedProductId: dryFood.product.id,
            productPercentage: this.wetPercentage
          })
      });
    }

    if (!this.shouldHideTreatsSelection && this.isCookieSelected && this.selectedCookie) {
      if (this.getCookieUnity() < 0.5) {
        this.isCookieSelected = false;
        return;
      }
      selectedAlimentation.push({
        id: this.selectedCookie.product.id,
        name: this.selectedCookie.product.name,
        sku: this.selectedCookie.product.sku,
        shortName: this.selectedCookie.product.shortName,
        type: this.selectedCookie.product.productTypeId,
        imageUrl: this.selectedCookie.product.productImageUrl,
        premierSuggestionQty: this.getCookieUnity(),
        premierSuggestionPerDay: 1,
        vetQty: this.getCookieUnity(),
        vetPerDay: 1,
        isCompleteFood: this.selectedCookie.product.isCompleteFood,
        useCombinedCompleteProducts
      });
    }

    if (
      !this.shouldHideTreatsSelection &&
      this.isSnackWetFoodSelected &&
      this.selectedSnackWetFood
    ) {
      const hasCookieAndWetSnack =
        this.isCookieSelected && this.isSnackWetFoodSelected;
      const wetGramsValue = hasCookieAndWetSnack
        ? this.selectedSnackWetFood.cookieAndWetGramsValue
        : this.selectedSnackWetFood.wetGramsValue;

      selectedAlimentation.push({
        id: this.selectedSnackWetFood.product.id,
        name: this.selectedSnackWetFood.product.name,
        sku: this.selectedSnackWetFood.product.sku,
        shortName: this.selectedSnackWetFood.product.shortName,
        type: this.selectedSnackWetFood.product.productTypeId,
        imageUrl: this.selectedSnackWetFood.product.productImageUrl,
        premierSuggestionQty: wetGramsValue,
        premierSuggestionPerDay: 1,
        vetQty: wetGramsValue,
        vetPerDay: 1,
        isCompleteFood: this.selectedSnackWetFood.product.isCompleteFood,
        useCombinedCompleteProducts
      });
    }

    this.selectedAlimentation = selectedAlimentation;
    this.selectedAlimentationChanged.next(this.selectedAlimentation);
  }

  getAlimentationGrams(alimentation: ProductCalculationResult): number {
    if (
      this.isCookieSelected &&
      this.selectedCookie &&
      this.isSnackWetFoodSelected &&
      this.selectedSnackWetFood
    ) {
      return alimentation.cookieAndWetAmount;
    } else if (this.isCookieSelected && this.selectedCookie) {
      return alimentation.cookieAmount;
    } else if (this.isSnackWetFoodSelected && this.selectedSnackWetFood) {
      return alimentation.wetAmount;
    } else {
      return alimentation.gramsPerDay;
    }
  }

  getCookieUnity(): number {
    return this.isSnackWetFoodSelected
      ? this.selectedCookie.cookieAndWetUnitaryValue
      : this.selectedCookie.cookieUnitaryValue;
  }

  inicializePortionsForm(): void {
    this.clearPortionsForm();
    const premierPortions = this.portionsForm.controls
      .premierPortions as UntypedFormArray;
    const premierGrams = this.portionsForm.controls
      .premierGrams as UntypedFormArray;
    const portions = this.portionsForm.controls.portions as UntypedFormArray;
    const grams = this.portionsForm.controls.grams as UntypedFormArray;
    const premierPortionsWet = this.portionsForm.controls
      .premierPortionsWet as UntypedFormArray;
    const premierGramsWet = this.portionsForm.controls
      .premierGramsWet as UntypedFormArray;
    const _portionsWet = this.portionsForm.controls
      .portionsWet as UntypedFormArray;
    const _gramsWet = this.portionsForm.controls.gramsWet as UntypedFormArray;

    this.suggestedAlimentation.dryFood.forEach((a) => {
      premierPortions.push(new UntypedFormControl(a.suggestedPortionAmount));
      premierGrams.push(
        new UntypedFormControl(
          (a.gramsPerDay / a.suggestedPortionAmount).toFixed(0)
        )
      );
      portions.push(new UntypedFormControl(a.suggestedPortionAmount));
      grams.push(
        new UntypedFormControl(
          (a.gramsPerDay / a.suggestedPortionAmount).toFixed(0)
        )
      );
    });

    if (this.suggestedAlimentation.completeWetFood?.length) {
      this.suggestedAlimentation.completeWetFood.forEach((w) => {
        premierPortionsWet.push(
          new UntypedFormControl(w.suggestedPortionAmount || 1)
        );
        premierGramsWet.push(
          new UntypedFormControl(
            (w.gramsPerDay / (w.suggestedPortionAmount || 1)).toFixed(0)
          )
        );
        _portionsWet.push(
          new UntypedFormControl(w.suggestedPortionAmount || 1)
        );
        _gramsWet.push(
          new UntypedFormControl(
            (w.gramsPerDay / (w.suggestedPortionAmount || 1)).toFixed(0)
          )
        );
      });
    }
  }

  updateTypePrescription(): void {
    const selectedDryFood = this.suggestedAlimentation?.dryFood?.find(
      (a) => a.selected
    );
    const selectedCompleteWetFood = this.suggestedAlimentation?.completeWetFood?.find(
      (a) => a.selected
    );
    const alimentation = selectedDryFood ?? selectedCompleteWetFood;
    if (!alimentation?.product) return;

    const typePrescription =
      alimentation.product.productLineId === ProductLineEnum.CLINICAL_CONDITION
        ? OrientationTypeEnum.NC
        : OrientationTypeEnum.SP;

    this.typePrescriptionChanged.next(typePrescription);
  }

  screenWidth: number;
  mobileScreen: boolean;

  getdeviceType(): DeviceTypeEnum {
    switch (this.detectorDevice.deviceType) {
      case 'mobile':
        return DeviceTypeEnum.MOBILE;
      case 'tablet':
        return DeviceTypeEnum.TABLET;
      case 'desktop':
        return DeviceTypeEnum.DESKTOP;
      default:
        return DeviceTypeEnum.DESKTOP;
    }
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(): void {
    this.screenWidth = window.innerWidth;
    this.mobileScreen = this.screenWidth <= 768;
  }

  private foodCombinationWarnings() {
    if (!this.isDryFoodSelected && !this.isCompleteWetFoodSelected) {
      this.notificationService.warning(
        'Selecione ao menos uma Alimentação Completa.'
      );
    } else if (this.isFoodCombination) {
      this.notificationService.warning(
        'Defina a proporção da Alimentação Úmida para Alimentação Seca.'
      );
    }
  }

  onDryFoodCheckboxChanged(checked: boolean): void {
    if (!checked && this.isFoodCombination && this.dryPercentage != 0) {
      this.resetFoodPercentage();
      this.recalculateWetAndDryFoods(true);
    }
    this.isDryFoodSelected = checked;
    this.updateSelectedAlimentation();
    this.foodCombinationWarnings();
  }

  onCompleteWetFoodCheckboxChanged(checked: boolean): void {
    if (!checked && this.isFoodCombination && this.wetPercentage != 0) {
      this.resetFoodPercentage();
      this.recalculateWetAndDryFoods(true);
    }
    this.isCompleteWetFoodSelected = checked;
    this.suggestedAlimentation.completeWetFood[0].selected = true;
    this.updateSelectedAlimentation();
    this.foodCombinationWarnings();
  }

  onCookieCheckboxChanged(checked: boolean): void {
    this.isCookieSelected = checked;
    this.recalculateValuesWithThreats();
    this.updateSelectedAlimentation();
  }

  onSnackWetFoodCheckboxChanged(checked: boolean): void {
    this.isSnackWetFoodSelected = checked;
    this.recalculateValuesWithThreats();
    this.updateSelectedAlimentation();
  }

  resetFoodPercentage() {
    this.dryPercentage = 0;
    this.wetPercentage = 0;
  }

  recalculateValuesWithThreats(): void {
    if (this.isFoodCombination) {
      this.changeProduct(this.selectedDryFood, 'dry');
      this.changeProduct(this.selectedCompleteWetFood, 'wet');
    } else if (this.isDryFoodSelected) {
      this.changeProduct(this.selectedDryFood, 'dry');
    } else if (this.isCompleteWetFoodSelected) {
      this.changeProduct(this.selectedCompleteWetFood, 'wet');
    }
  }

  calculateProduct(
    product: Product,
    foodType: 'dry' | 'wet' | 'cookie' | 'snack'
  ): void {
    this.fetchProductsFromCalculator(
      { ...this.calculator, productId: product.id },
      (res) => {
        let productCalculated;
        switch (foodType) {
          case 'dry':
            if (res.dryFoodAlertMessage) {
              this.notificationService.warning(res.dryFoodAlertMessage);
            }
            productCalculated = res.dryFood[0];
            break;
          case 'wet':
            productCalculated = res.completeWetFood[0];
            break;
          case 'cookie':
            productCalculated = res.cookies[0];
            break;
          case 'snack':
            productCalculated = res.wetFood[0];
            break;
        }

        this.changeProduct(productCalculated, foodType);
      }
    );
  }

  changeProduct(
    alimentation: ProductCalculationResult,
    foodType: 'dry' | 'wet' | 'cookie' | 'snack'
  ): void {
    switch (foodType) {
      case 'dry': {
        const index = this.suggestedAlimentation.dryFood.findIndex(
          (alimentation) => alimentation.selected
        );
        if (alimentation.product.productLineId === ProductLineEnum.CLINICAL_CONDITION) {
          this.suggestedAlimentation.dryFood = [
            { ...alimentation, selected: true }
          ];
        } else {
          this.suggestedAlimentation.dryFood[index] = {
            ...alimentation,
            selected: true
          };
        }

        this.listGradualExchanges = this.suggestedAlimentation.dryFood[index];
        const originalGramsPerDay = this.originalDryFoodGrams;

        this.selectedDryFood = this.suggestedAlimentation.dryFood[index];

        const hasCookie = this.isCookieSelected && !this.isSnackWetFoodSelected;
        const hasWetSnack = this.isSnackWetFoodSelected && !this.isCookieSelected;
        const hasCookieAndWet = this.isCookieSelected && this.isSnackWetFoodSelected;

        if (hasCookie) {
          this.selectedDryFood.gramsPerDay = this.selectedDryFood.cookieAmount;
        } else if (hasWetSnack) {
          this.selectedDryFood.gramsPerDay = this.selectedDryFood.wetAmount;
        } else if (hasCookieAndWet) {
          this.selectedDryFood.gramsPerDay =
            this.selectedDryFood.cookieAndWetAmount;
        } else {
          this.selectedDryFood.gramsPerDay = originalGramsPerDay;
        }

        this.inicializePortionsForm();

        if (this.isCompleteWetFoodSelected) {
          this.addCompleteWetFood();
        }

        if (this.isCookieSelected) {
          this.addCookie();
        }

        if (this.isSnackWetFoodSelected) {
          this.addSnackWetFood();
        }
        break;
      }
      case 'wet': {
        alimentation = {
          ...alimentation
        };
        const index = this.suggestedAlimentation.completeWetFood.findIndex(
          (alimentation) =>
            alimentation.product.id === this.selectedCompleteWetFood.product.id
        );
        if (alimentation.product.productLineId === ProductLineEnum.CLINICAL_CONDITION) {
           this.suggestedAlimentation.completeWetFood = [
            { ...alimentation, selected: true }
          ];
        } else {
          this.suggestedAlimentation.completeWetFood[index] = {
            ...alimentation,
            selected: true
          };
        }
        const hasCookieAndWetSnack =
          this.isCookieSelected && this.isSnackWetFoodSelected;
        const hasCookie = this.isCookieSelected && !this.isSnackWetFoodSelected;
        const hasWetSnack =
          this.isSnackWetFoodSelected && !this.isCookieSelected;
        if (this.isFoodCombination) {
          const wetGramsValue = hasCookieAndWetSnack
            ? alimentation.cookieAndWetAmount
            : alimentation.gramsPerDay;
          this.minQuantityWetFoodNotReached = wetGramsValue <= 0;
          if (this.minQuantityWetFoodNotReached) {
            this.dryPercentage = 0;
            this.wetPercentage = 0;
          }
        }
        if (hasCookie && this.isCompleteWetFoodSelected) {
          alimentation.gramsPerDay = alimentation.cookieAmount;
        } else if (hasWetSnack && this.isCompleteWetFoodSelected) {
          alimentation.gramsPerDay = alimentation.wetAmount;
        } else if (hasCookieAndWetSnack && this.isCompleteWetFoodSelected) {
          alimentation.gramsPerDay = alimentation.cookieAndWetAmount;
        } else {
          alimentation.gramsPerDay = this.originalWetFoodGrams;
        }
        this.selectedCompleteWetFood = alimentation;
        this.inicializePortionsForm();
        break;
      }
      case 'cookie':
        this.selectedCookie = alimentation;
        break;
      case 'snack':
        this.selectedSnackWetFood = alimentation;
        break;
    }

    this.updateTypePrescription();
    this.updateSelectedAlimentation();
  }

  addCompleteWetFood(): void {
    this.isCompleteWetFoodSelected = true;
    if (!this.suggestedAlimentation.completeWetFood?.length) {
      this.alertNoComplementaryFoodOffer(TypeProductEnum.WET_FOOD);
      return;
    }
  }

  addCookie(): void {
    this.isCookieSelected = true;
    if (!this.suggestedAlimentation.cookies?.length) {
      this.alertNoComplementaryFoodOffer(TypeProductEnum.COOKIE);
      return;
    }
    this.updateSelectedAlimentation();
    this.updatePortionsForm();
  }

  updatePortionsForm(): void {
    const premierPortions = this.portionsForm.controls
      .premierPortions as UntypedFormArray;
    const premierGrams = this.portionsForm.controls
      .premierGrams as UntypedFormArray;
    const portions = this.portionsForm.controls.portions as UntypedFormArray;
    const grams = this.portionsForm.controls.grams as UntypedFormArray;

    this.suggestedAlimentation.dryFood.forEach((a, index) => {
      let alimentationGrams = a.gramsPerDay;
      if (this.isCookieSelected && this.isSnackWetFoodSelected) {
        alimentationGrams = a.cookieAndWetAmount;
      } else if (this.isCookieSelected) {
        alimentationGrams = a.cookieAmount;
      } else if (this.isSnackWetFoodSelected) {
        alimentationGrams = a.wetAmount;
      }

      const premierPortion = premierPortions.controls[index].value;
      const premierGram = premierGrams.controls[index].value;
      const portion = portions.controls[index].value;
      const gram = grams.controls[index].value;
      if (premierPortion === portion && premierGram === gram) {
        premierPortions.controls[index].setValue(a.suggestedPortionAmount);
        premierGrams.controls[index].setValue(
          (alimentationGrams / a.suggestedPortionAmount).toFixed(0)
        );
        portions.controls[index].setValue(a.suggestedPortionAmount);
        grams.controls[index].setValue(
          (alimentationGrams / a.suggestedPortionAmount).toFixed(0)
        );
      }
    });

    if (this.suggestedAlimentation.completeWetFood?.length) {
      const premierPortionsWetArr = this.portionsForm.controls
        .premierPortionsWet as UntypedFormArray;
      const premierGramsWetArr = this.portionsForm.controls
        .premierGramsWet as UntypedFormArray;
      const portionsWetArr = this.portionsForm.controls
        .portionsWet as UntypedFormArray;
      const gramsWetArr = this.portionsForm.controls
        .gramsWet as UntypedFormArray;

      this.suggestedAlimentation.completeWetFood.forEach((w, wIndex) => {
        const premierPortionWet =
          premierPortionsWetArr?.controls[wIndex]?.value;
        const premierGramWet = premierGramsWetArr?.controls[wIndex]?.value;
        const portionWet = portionsWetArr?.controls[wIndex]?.value;
        const gramWet = gramsWetArr?.controls[wIndex]?.value;

        if (premierPortionWet === portionWet && premierGramWet === gramWet) {
          if (premierPortionsWetArr?.controls[wIndex]) {
            premierPortionsWetArr.controls[wIndex].setValue(
              w.suggestedPortionAmount || 1
            );
          }
          if (premierGramsWetArr?.controls[wIndex]) {
            premierGramsWetArr.controls[wIndex].setValue(
              (w.gramsPerDay / (w.suggestedPortionAmount || 1)).toFixed(0)
            );
          }
          if (portionsWetArr?.controls[wIndex]) {
            portionsWetArr.controls[wIndex].setValue(
              w.suggestedPortionAmount || 1
            );
          }
          if (gramsWetArr?.controls[wIndex]) {
            gramsWetArr.controls[wIndex].setValue(
              (w.gramsPerDay / (w.suggestedPortionAmount || 1)).toFixed(0)
            );
          }
        }
      });
    }

    this.updateSelectedAlimentation();
  }

  addSnackWetFood(): void {
    this.isSnackWetFoodSelected = true;
    if (!this.suggestedAlimentation.wetFood?.length) {
      this.alertNoComplementaryFoodOffer(TypeProductEnum.WET_FOOD);
      return;
    }
    this.updateSelectedAlimentation();
    this.updatePortionsForm();
  }

  private alertNoComplementaryFoodOffer(typeProduct: TypeProductEnum): void {
    const product =
      typeProduct === TypeProductEnum.COOKIE ? 'cookies' : 'alimentos úmidos';
    const specie = this.petSelected.petEspecie === 'Dog' ? 'cães' : 'gatos';
    const ageGroup = this.getAgeGroupDescription(
      this.petSelected.faixaEtariaId
    );

    const message = `No momento a PremieRpet® não possui ofertas de ${product} para ${specie} ${ageGroup}.`;
    this.notificationService.warning(message);
  }

  private getAgeGroupDescription(ageGroupId: AgeGroupEnum): string {
    switch (ageGroupId) {
      case AgeGroupEnum.PUPPY:
        return 'filhotes';
      case AgeGroupEnum.ADULT:
        return 'adultos';
      case AgeGroupEnum.SENIOR:
        return 'sêniors';
    }
  }

  onDryFoodProductSelected(product: Product): void {
    this.calculateProduct(product, 'dry');
  }

  onCompleteWetFoodProductSelected(product: Product): void {
    this.calculateProduct(product, 'wet');
  }

  onCookieProductSelected(product: Product): void {
    this.calculateProduct(product, 'cookie');
  }

  onSnackWetFoodProductSelected(product: Product): void {
    this.calculateProduct(product, 'snack');
  }

  onPercentageChange(percentages: {
    wetPercentage: number;
    dryPercentage: number;
  }) {
    this.wetPercentage = percentages.wetPercentage;
    this.dryPercentage = percentages.dryPercentage;
    this.validateCombineFood();
    this.recalculateWetAndDryFoods();
  }

  onDryFoodPortionsChanged(portions: {
    portions: number;
    grams: number;
  }): void {
    if (this.selectedDryFood) {
      const indexSuggestedAlimentation =
        this.suggestedAlimentation?.dryFood?.findIndex((x) => x.selected);
      if (indexSuggestedAlimentation >= 0) {
        const portionsArray = this.portionsForm.controls
          .portions as UntypedFormArray;
        const gramsArray = this.portionsForm.controls.grams as UntypedFormArray;

        portionsArray.controls[indexSuggestedAlimentation].setValue(
          portions.portions
        );
        gramsArray.controls[indexSuggestedAlimentation].setValue(
          portions.grams
        );
      }

      this.updateSelectedAlimentation();
    }
  }

  onCompleteWetFoodPortionsChanged(portions: {
    portions: number;
    grams: number;
  }): void {
    if (this.selectedCompleteWetFood) {
      const indexSuggestedAlimentation =
        this.suggestedAlimentation?.completeWetFood?.findIndex(
          (x) => x.product.id === this.selectedCompleteWetFood.product.id
        );
      if (indexSuggestedAlimentation >= 0) {
        const portionsArrayWet = this.portionsForm.controls
          .portionsWet as UntypedFormArray;
        const gramsArrayWet = this.portionsForm.controls
          .gramsWet as UntypedFormArray;

        if (
          portionsArrayWet &&
          portionsArrayWet.controls[indexSuggestedAlimentation]
        ) {
          portionsArrayWet.controls[indexSuggestedAlimentation].setValue(
            portions.portions
          );
        }
        if (
          gramsArrayWet &&
          gramsArrayWet.controls[indexSuggestedAlimentation]
        ) {
          gramsArrayWet.controls[indexSuggestedAlimentation].setValue(
            portions.grams
          );
        }
      }

      this.updateSelectedAlimentation();
    }
  }

  recalculateWetAndDryFoods(isResetingValues?: boolean): void {
    this.isRecalculatingValues = true;
    if (isResetingValues) {
      this.fetchProductsFromCalculator(
        {
          ...this.calculator
        },
        (res) => {
          this.originalDryFoodGrams = res.dryFood[0].gramsPerDay;
          this.originalWetFoodGrams = res.completeWetFood[0].gramsPerDay;
          this.changeProduct(res.dryFood[0], 'dry');
          this.changeProduct(res.completeWetFood[0], 'wet');
          this.changeProduct(res.cookies[0], 'cookie');
          this.changeProduct(res.wetFood[0], 'snack');
          this.isRecalculatingValues = false;
          this.isCookieSelected = false;
          this.isSnackWetFoodSelected = false;
        }
      );
    } else {
      this.fetchProductsFromCalculator(
        {
          ...this.calculator,
          productId: this.suggestedAlimentation.dryFood[0].product.id
        },
        (res) => {
          console.log('retorno após a combinação: ', res);
          this.originalDryFoodGrams = res.dryFood[0].gramsPerDay;
          this.originalWetFoodGrams = res.completeWetFood[0].gramsPerDay;
          this.changeProduct(res.dryFood[0], 'dry');
          this.changeProduct(res.completeWetFood[0], 'wet');
          this.isRecalculatingValues = false;
        }
      );
    }
  }

  validateCompleteFood(): boolean {
    return this.isDryFoodSelected || this.isCompleteWetFoodSelected;
  }

  validateCombineFood(): boolean {
    if (this.isFoodCombination) {
      const wet = this.wetPercentage;
      const dry = this.dryPercentage;
      if (wet < 1 || wet > 99 || dry < 1 || dry > 99) {
        this.combineFoodError = true;
        return false;
      }
    }
    this.combineFoodError = false;
    return true;
  }

  // getAssistedNutritionalSupportMessage(): string | null {
  //   if (
  //     this.nutritionalGuidanceForm?.controls?.orientationPurposeId?.value ===
  //       OrientationPurposeEnum.DOG_ASSISTED_NUTRITIONAL_SUPPORT ||
  //     this.nutritionalGuidanceForm?.controls?.orientationPurposeId?.value ===
  //       OrientationPurposeEnum.CAT_ASSISTED_NUTRITIONAL_SUPPORT
  //   ) {
  //     if (
  //       this.nutritionalGuidanceForm?.controls?.nivelAtividade?.value === 14
  //     ) {
  //       return `
  //         Caso opte por utilizar um alimento seco, recomendamos a diluição na proporção 1:3 ou 1:4 (alimento : água morna). Aguarde o grão amolecer e bata com liquidificador ou mixer para alcançar a consistência líquida e peneire para remover resíduos sólidos. Recomendamos que só seja utilizado alimento seco para sondas nasoesofágicas a partir de 10 Fr.
  //         <br/>
  //         Para o alimento PNC Recuperação faça a diluição na proporção 1:0,5 (alimento : água) para ser administrado em sondas a partir de 10 french. Utilize água morna para facilitar a homogeneização.
  //       `;
  //     }
  //     if (
  //       this.nutritionalGuidanceForm?.controls?.nivelAtividade?.value === 15
  //     ) {
  //       return `
  //         Caso opte por utilizar um alimento seco, recomendamos a diluição na proporção 1:3 ou 1:4 (alimento : água morna). Aguarde o grão amolecer e bata com liquidificador ou mixer para alcançar a consistência líquida.
  //         <br/>
  //         O alimento PNC Recuperação não precisa ser diluído para esse tipo de sonda.
  //       `;
  //     }
  //   }
  //   return null;
  // }

  get shouldHideTreatsSelection(): boolean {
    return [
      PurposeEnum.DOG_WEANING,
      PurposeEnum.CAT_WEANING,
      OrientationPurposeEnum.DOG_ASSISTED_NUTRITIONAL_SUPPORT,
      OrientationPurposeEnum.CAT_ASSISTED_NUTRITIONAL_SUPPORT
    ].includes(this.nutritionalGuidanceForm?.controls?.orientationPurposeId?.value);
  }

  get shouldShowEccMessage(): boolean {
    const eccScoreValue = this.nutritionalGuidanceForm.controls.score.value;
    const eccScoreGroupMessage = [ 1, 2, 3 ];
    return [
      PurposeEnum.DOG_PREGNANT,
      PurposeEnum.CAT_PREGNANT
    ].includes(this.nutritionalGuidanceForm?.controls?.orientationPurposeId?.value) && eccScoreGroupMessage.includes(eccScoreValue) || this.isBaby && eccScoreGroupMessage.includes(eccScoreValue);
  }

  get shouldShowAbove12YearsProductMessage(): boolean {
    const catOsteoarticularDisease = PurposeEnum.CAT_OSTEOARTICULAR_DISEASES;
    const above12YearsProductSku = '2008-042';
    const calculatorPurposeResult = this.nutritionalGuidanceForm?.controls?.orientationPurposeId?.value;
    const dryFoodResultSku = this.suggestedAlimentation?.dryFood[0]?.product?.sku;

    if (dryFoodResultSku === above12YearsProductSku && calculatorPurposeResult === catOsteoarticularDisease) {
      return true;
    }
    return false;
  }

  getClinicalNutritionMessage(): string | null {
    return (
      (this.isDryFoodSelected &&
        this.selectedDryFood?.clinicalNutritionMessage?.message) ||
      (this.isCompleteWetFoodSelected &&
        this.selectedCompleteWetFood?.clinicalNutritionMessage?.message) ||
      null
    );
  }

  addGradualChange(): void {
    this.gradualExchange = !this.gradualExchange;
    this.nutritionalGuidanceForm.controls.gradualExchange.setValue(
      this.gradualExchange
    );
  }
}
